// Phase 21 · Video room view (Zoom-style grid, avatar placeholders)
import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Radio, PlayCircle, LogIn, LogOut, Users } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../../context/AuthContext";
import useEventPolling from "../../../hooks/useEventPolling";
import ParticipantTile from "../../../components/community_v4/ParticipantTile";
import ReactionsBar from "../../../components/community_v4/ReactionsBar";
import RoomChatPanel from "../../../components/community_v4/RoomChatPanel";
import HostControlsPanel from "../../../components/community_v4/HostControlsPanel";

const API = process.env.REACT_APP_BACKEND_URL;

export default function VideoRoomView() {
    const { roomId } = useParams();
    const { user } = useAuth();
    const [room, setRoom] = useState(null);
    const [loading, setLoading] = useState(true);
    const [disabled, setDisabled] = useState(false);
    const [busy, setBusy] = useState(false);
    const [spotlightId, setSpotlightId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true); setDisabled(false);
        try {
            const r = await fetch(`${API}/api/community/rooms/${roomId}`, { credentials: "include" });
            if (r.status === 503) { setDisabled(true); return; }
            if (r.status === 404) { setRoom(null); return; }
            if (r.ok) setRoom(await r.json());
        } catch {}
        finally { setLoading(false); }
    }, [roomId]);
    useEffect(() => { load(); }, [load]);

    const { data: partsData, refetch: refetchParts } = useEventPolling(
        room ? `${API}/api/community/rooms/${room.id}/participants` : null,
    );
    const { data: reqsData, refetch: refetchReqs } = useEventPolling(
        room?.is_host ? `${API}/api/community/rooms/${room.id}/speaker-requests?status=pending` : null,
    );

    const parts = partsData?.items || [];
    const spotlight = spotlightId
        ? parts.find((p) => p.user_id === spotlightId)
        : parts.find((p) => p.role === "host");
    const grid = parts.filter((p) => p.user_id !== spotlight?.user_id);
    const reqs = reqsData?.items || [];

    const joinRoom = async () => {
        setBusy(true);
        try {
            const r = await fetch(`${API}/api/community/rooms/${roomId}/join`, { method: "POST", credentials: "include" });
            if (r.ok) { toast.success("Joined"); load(); refetchParts(); }
            else if (r.status === 422) {
                const d = await r.json();
                toast.error(d?.detail?.error === "room_full" ? "Room full" : "Cannot join");
            }
        } catch {}
        setBusy(false);
    };
    const leaveRoom = async () => {
        setBusy(true);
        try {
            const r = await fetch(`${API}/api/community/rooms/${roomId}/leave`, { method: "POST", credentials: "include" });
            if (r.ok) { toast.success("Left"); load(); refetchParts(); }
        } catch {}
        setBusy(false);
    };
    const goLive = async () => {
        setBusy(true);
        try {
            const r = await fetch(`${API}/api/community/rooms/${roomId}/go-live`, { method: "POST", credentials: "include" });
            if (r.ok) { toast.success("You're live!"); load(); }
        } catch {}
        setBusy(false);
    };
    const toggleMute = async (p) => {
        try {
            const r = await fetch(`${API}/api/community/rooms/${roomId}/participants/${p.user_id}/toggle-mute`, { method: "POST", credentials: "include" });
            if (r.ok) refetchParts();
        } catch {}
    };

    if (loading) return <div className="min-h-screen bg-[#0A0A0A] text-neutral-500 flex items-center justify-center">Loading…</div>;
    if (disabled) return (
        <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-6 text-center">
            <div><p className="text-[#FAFAFA] mb-2">Rooms engine disabled</p><Link to="/community" className="text-neutral-400 underline">← Community</Link></div>
        </div>
    );
    if (!room) return (
        <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-6 text-center">
            <div><p className="text-neutral-500 mb-2">Room not found</p><Link to="/rooms" className="text-[#FFFFFF]">← Rooms</Link></div>
        </div>
    );

    const isParticipant = room.viewer_role != null;

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-[#FFFFFF] font-inter" data-testid="video-room-page">
            <header className="border-b border-[#1A1A1A] backdrop-blur-md bg-[#0A0A0A]/80 sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
                    <Link to="/rooms" className="text-neutral-400 hover:text-neutral-200 flex items-center gap-1 text-sm">
                        <ArrowLeft size={16} /> Rooms
                    </Link>
                    <span className="text-neutral-600">·</span>
                    <div className="flex items-center gap-2 min-w-0">
                        <h1 className="text-base font-semibold truncate">{room.title}</h1>
                        {room.status === "live" && (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#FAFAFA] text-black font-bold animate-pulse">
                                <Radio size={9} /> LIVE
                            </span>
                        )}
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                        {room.is_host && room.status === "scheduled" && (
                            <button type="button" onClick={goLive} disabled={busy} className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#FAFAFA] hover:bg-[#FAFAFA]/80 text-white font-semibold rounded-lg text-xs" data-testid="room-go-live-btn">
                                <PlayCircle size={13} /> Go live
                            </button>
                        )}
                        {!room.is_host && room.status === "live" && !isParticipant && user && (
                            <button type="button" onClick={joinRoom} disabled={busy} className="inline-flex items-center gap-1 px-3 py-1.5 bg-white text-neutral-900 font-semibold rounded-lg text-xs" data-testid="room-join-btn">
                                <LogIn size={13} /> Join
                            </button>
                        )}
                        {!room.is_host && isParticipant && (
                            <button type="button" onClick={leaveRoom} disabled={busy} className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#1A1A1A] hover:border-[#FAFAFA]/40 hover:text-[#FAFAFA] text-neutral-400 rounded-lg text-xs" data-testid="room-leave-btn">
                                <LogOut size={13} /> Leave
                            </button>
                        )}
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
                <section className="space-y-4">
                    <div className="bg-black border border-[#1A1A1A] rounded-2xl p-4 space-y-4">
                        {spotlight && (
                            <ParticipantTile p={spotlight} spotlight isHostView={room.is_host} onToggleMute={toggleMute} />
                        )}
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                            {grid.map((p) => (
                                <div key={p.id} onClick={() => setSpotlightId(p.user_id)} className="cursor-pointer">
                                    <ParticipantTile p={p} isHostView={room.is_host} onToggleMute={toggleMute} />
                                </div>
                            ))}
                        </div>
                        {!parts.length && (
                            <p className="text-center text-sm text-neutral-500 italic py-6">No participants yet</p>
                        )}
                        <p className="text-[10px] text-neutral-500 flex items-center gap-1 justify-center">
                            <Users size={11} /> {parts.length}/{room.max_participants} · Camera feeds are placeholders (V4 UI-only, no WebRTC)
                        </p>
                    </div>
                    {isParticipant && room.status === "live" && (
                        <ReactionsBar roomId={room.id} canReact={true} />
                    )}
                </section>

                <aside className="space-y-4 lg:sticky lg:top-[68px] lg:self-start h-[720px]">
                    {room.is_host && <HostControlsPanel room={room} requests={reqs} onRefresh={() => { load(); refetchParts(); refetchReqs(); }} />}
                    <RoomChatPanel room={room} viewer={user} canSend={isParticipant} />
                </aside>
            </main>
        </div>
    );
}
